<template>
  <MBaseModal
    body-background-color="bg-primary-100"
    :height="status === 'display' ? 'tall' : 'normal'"
    @close="close"
  >
    <template #title>{{
      translatedValue(generateObjectPath("title"))
    }}</template>
    <template #body>
      <main
        class="w-full h-full relative pt-20 sm:pt-0"
        :class="{ 'pt-none': status !== 'display' }"
      >
        <div v-if="status === 'display'" class="reflection-wrapper">
          <section class="mb-8">
            <h1 class="text-2xl font-bold mb-2">
              {{
                translatedValue(generateObjectPath("contentsOf"), { unitName })
              }}
            </h1>
            <div class="mb-4">
              <h2 class="text-xl mb-1">{{ graphTitle }}</h2>
              <MSLineChart
                v-if="reflections.length > 0"
                class="line-chart-canvas"
                :labels="labels"
                :datasets="datasets"
                :min="min"
                :max="max"
              />
              <div
                v-else
                class="flex items-center justify-center w-4/5 h-24 mx-auto bg-white rounded-lg text-base text-center text-gray-500"
              >
                {{ translatedValue(generateObjectPath("noDataMessage")) }}
              </div>
            </div>
            <div class="mb-4">
              <h2 class="text-xl mb-1">{{ secondRowTitle }}</h2>
              <div class="flex flex-col w-4/5 mx-auto">
                <table class="border-collapse w-full">
                  <tr class="bg-gray-300">
                    <th class="w-1/5 border font-medium border-gray-400 p-1">
                      {{ translatedValue(generateObjectPath("dateHeading")) }}
                    </th>
                    <th class="w-4/5 border font-medium border-gray-400 p-1">
                      {{
                        translatedValue(generateObjectPath("contentHeading"))
                      }}
                    </th>
                  </tr>
                  <tr v-for="(r, i) in reflections" :key="i" class="bg-white">
                    <td class="w-1/5 border border-gray-400 p-1">
                      {{ formatDate(r.data.uid) }}
                    </td>
                    <td class="w-4/5 border border-gray-400 p-1 ">
                      <div class="w-full flex flex-row gap-1 items-center">
                        <span
                          class="block flex-1 break-all whitespace-normal"
                          >{{ r.data.reflection }}</span
                        >
                        <MIcon
                          type="pencil"
                          class="text-gray-500 mr-1 cursor-pointer"
                          @click="openRecordEditInput(r.data.uid)"
                        />
                        <MIcon
                          v-if="hasPolarDeviceid"
                          type="heart"
                          class="text-gray-500 mr-1 cursor-pointer"
                          @click="openHeartRateData(r.data.uid)"
                        />
                      </div>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
            <div>
              <h2 class="text-xl mb-1">{{ thirdRowTitle }}</h2>
              <div class="flex flex-col w-4/5 mx-auto">
                <table class="border-collapse w-full">
                  <tr class="bg-gray-300">
                    <th class="w-1/5 border font-medium border-gray-400 p-1">
                      {{ translatedValue(generateObjectPath("dateHeading")) }}
                    </th>
                    <th class="w-4/5 border font-medium border-gray-400 p-1">
                      {{
                        translatedValue(generateObjectPath("contentHeading"))
                      }}
                    </th>
                  </tr>
                  <tr v-for="(r, i) in reflections" :key="i" class="bg-white">
                    <td class="w-1/5 border border-gray-400 p-1">
                      {{ formatDate(r.data.uid) }}
                    </td>
                    <td class="w-4/5 border border-gray-400 p-1 ">
                      <div class="w-full flex flex-row gap-1 items-center">
                        <span
                          class="block flex-1 break-all whitespace-normal"
                          >{{ r.data.description }}</span
                        >
                        <MIcon
                          type="pencil"
                          class="text-gray-500 mr-1 cursor-pointer"
                          @click="openRecordEditInput(r.data.uid)"
                        />
                        <MIcon
                          v-if="hasPolarDeviceid"
                          type="heart"
                          class="text-gray-500 mr-1 cursor-pointer"
                          @click="openHeartRateData(r.data.uid)"
                        />
                      </div>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </section>
          <section>
            <h1 class="text-2xl font-bold mb-2">
              {{ translatedValue(generateObjectPath("unitReflectionHeading")) }}
            </h1>
            <div class="mb-4">
              <h2 class="text-xl mb-1">
                {{ translatedValue(generateObjectPath("selfReflection")) }}
              </h2>
              <div
                class="w-4/5 mx-auto bg-white p-2 break-all whitespace-pre-line"
                :class="{ 'text-gray-500': !unit || !unit.data.selfReflection }"
              >
                {{
                  unit?.data.selfReflection ||
                    translatedValue(generateObjectPath("noCommentMessage"))
                }}
              </div>
            </div>
            <div class="mb-4">
              <h2 class="text-xl mb-1">
                {{ translatedValue(generateObjectPath("teacherReflection")) }}
              </h2>
              <div
                class="w-4/5 mx-auto bg-white p-2 break-all whitespace-pre-line"
                :class="{
                  'text-gray-500': !unit || !unit.data.teacherReflection
                }"
              >
                {{
                  unit?.data.teacherReflection ||
                    translatedValue(generateObjectPath("noCommentMessage"))
                }}
              </div>
            </div>
            <div class="pb-4">
              <div class="flex flex-row items-center gap-4 mb-1">
                <h2 class="text-xl">
                  {{ translatedValue(generateObjectPath("aiReflection")) }}
                </h2>
                <div class="flex flex-row items-center gap-2">
                  <button
                    v-if="reflections.length > 0"
                    class="w-20 bg-white rounded-md py-1 border text-sm flex items-center justify-center "
                    :class="[
                      unit && unit.data.selfReflection
                        ? 'border-secondary-500 text-secondary-500 hover:bg-gray-100 focus:outline-none transition-all duration-300'
                        : 'border-gray-500 text-gray-500 cursor-default'
                    ]"
                    :disabled="!(unit && unit.data.selfReflection)"
                    @click.prevent="generateAiReflection"
                  >
                    {{ translatedValue(generateObjectPath("generate")) }}
                  </button>
                  <button
                    v-if="unit && unit.data.aiReflection"
                    class="w-20 bg-white rounded-md py-1 border border-red-500 text-red-500 text-sm flex items-center justify-center hover:bg-gray-100 focus:outline-none transition-all duration-300"
                    @click.prevent="deleteAiReflection"
                  >
                    {{ translatedValue(generateObjectPath("delete")) }}
                  </button>
                </div>
              </div>
              <p class="text-sm mb-1">
                {{ translatedValue(generateObjectPath("cautionForAiContent")) }}
              </p>
              <p
                v-if="
                  reflections.length > 0 && unit && !unit.data.selfReflection
                "
                class="text-xs text-gray-600 pb-1"
              >
                {{
                  translatedValue(
                    generateObjectPath("cautionForAiReflectionGeneration")
                  )
                }}
              </p>
              <Transition name="fade">
                <p
                  v-if="promptTrimmed"
                  class="w-4/5 mx-auto text-base text-center text-red-500 my-2"
                >
                  {{
                    `データが${getMaxLength()}文字を超えたのでプロンプトの一部が切り捨てられました。`
                  }}
                </p>
              </Transition>
              <div
                class="w-4/5 mx-auto bg-white p-2 break-all whitespace-pre-line"
                :class="{
                  'text-gray-500': !unit || !unit.data.aiReflection
                }"
              >
                {{
                  unit?.data.aiReflection ||
                    translatedValue(generateObjectPath("noCommentMessage"))
                }}
              </div>
            </div>
          </section>
        </div>
        <div
          v-else-if="status === 'recordInput'"
          class="reflection-wrapper flex flex-col gap-8"
        >
          <div class="w-4/5 mx-auto flex flex-col gap-2">
            <h2 class="font-normal text-lg">
              {{ translatedValue(generateObjectPath("dateOfClass")) }}
            </h2>
            <div class="flex flex-row items-center gap-2">
              <label class="text-sm w-16">開始:</label>
              <input
                type="datetime-local"
                class="flex-1 border border-gray-300 rounded px-2 py-1"
                :value="recordInput.timeRangeStart"
                @input="recordInput.timeRangeStart = $event.target.value"
              />
            </div>
            <div class="flex flex-row items-center gap-2">
              <label class="text-sm w-16">終了:</label>
              <input
                type="datetime-local"
                class="flex-1 border border-gray-300 rounded px-2 py-1"
                :value="recordInput.timeRangeEnd"
                @input="recordInput.timeRangeEnd = $event.target.value"
              />
            </div>
            <p v-if="!validTimeRangeInput" class="text-red-500 text-sm">
              開始日時は終了日時より前である必要があります。
            </p>
          </div>
          <div class="w-4/5 mx-auto flex flex-row items-center">
            <h2 class="font-normal text-lg w-32">{{ graphTitle }}</h2>
            <MTextField
              type="number"
              :value="recordInput.record"
              :entered="recordEntered"
              :invalid="!validRecordInput"
              invalid-message="値が不正です。"
              @input="recordInput.record = $event"
              @enter="
                () => {
                  recordEntered = true;
                  if (recordInput.record !== 0 && !recordInput.record) {
                    validRecordInput = false;
                  } else {
                    validRecordInput = true;
                  }
                }
              "
            />
          </div>
          <div class="w-4/5 mx-auto flex flex-row items-center">
            <h2 class="font-normal text-lg w-32">{{ secondRowTitle }}</h2>
            <MTextField
              type="text"
              :value="recordInput.reflection"
              :invalid="!validRecordReflectionInput"
              invalid-message="振り返りを入力してください。"
              :entered="recordReflectionEntered"
              @enter="
                () => {
                  recordReflectionEntered = true;
                  if (recordInput.reflection.length > 0) {
                    validRecordReflectionInput = true;
                  } else {
                    validRecordReflectionInput = false;
                  }
                }
              "
              @input="recordInput.reflection = $event"
            />
          </div>
          <div class="w-4/5 mx-auto flex flex-row items-center">
            <h2 class="font-normal text-lg w-32">{{ thirdRowTitle }}</h2>
            <MTextField
              type="text"
              :value="recordInput.description"
              :invalid="!validRecordDescriptionInput"
              invalid-message="入力してください。"
              :entered="recordDescriptionEntered"
              @enter="
                () => {
                  recordDescriptionEntered = true;
                  if (recordInput.description.length > 0) {
                    validRecordDescriptionInput = true;
                  } else {
                    validRecordDescriptionInput = false;
                  }
                }
              "
              @input="recordInput.description = $event"
            />
          </div>
        </div>
        <div
          v-else-if="status === 'recordEdit'"
          class="reflection-wrapper flex flex-col gap-8"
        >
          <div class="w-4/5 mx-auto flex flex-col gap-2">
            <h2 class="font-normal text-lg">
              {{ translatedValue(generateObjectPath("dateOfClass")) }}
            </h2>
            <div class="flex flex-row items-center gap-2">
              <label class="text-sm w-16">開始:</label>
              <input
                type="datetime-local"
                class="flex-1 border border-gray-300 rounded px-2 py-1"
                :value="eRecordInput.timeRangeStart"
                @input="eRecordInput.timeRangeStart = $event.target.value"
              />
            </div>
            <div class="flex flex-row items-center gap-2">
              <label class="text-sm w-16">終了:</label>
              <input
                type="datetime-local"
                class="flex-1 border border-gray-300 rounded px-2 py-1"
                :value="eRecordInput.timeRangeEnd"
                @input="eRecordInput.timeRangeEnd = $event.target.value ?? ''"
              />
            </div>
            <p v-if="!eValidTimeRangeInput" class="text-red-500 text-sm">
              開始日時は終了日時より前である必要があります。
            </p>
          </div>
          <div class="w-4/5 mx-auto flex flex-row items-center">
            <h2 class="font-normal text-lg w-32">{{ graphTitle }}</h2>
            <MTextField
              type="number"
              :value="eRecordInput.record"
              :entered="eRecordEntered"
              :invalid="!eValidRecordInput"
              invalid-message="値が不正です。"
              @input="eRecordInput.record = $event"
              @enter="
                () => {
                  eRecordEntered = true;
                  if (eRecordInput.record !== 0 && !eRecordInput.record) {
                    eValidRecordInput = false;
                  } else {
                    eValidRecordInput = true;
                  }
                }
              "
            />
          </div>
          <div class="w-4/5 mx-auto flex flex-row items-center">
            <h2 class="font-normal text-lg w-32">{{ secondRowTitle }}</h2>
            <MTextField
              type="text"
              :value="eRecordInput.reflection"
              :invalid="!eValidRecordReflectionInput"
              invalid-message="振り返りを入力してください。"
              :entered="eRecordReflectionEntered"
              @enter="
                () => {
                  eRecordReflectionEntered = true;
                  if (eRecordInput.reflection.length > 0) {
                    eValidRecordReflectionInput = true;
                  } else {
                    eValidRecordReflectionInput = false;
                  }
                }
              "
              @input="eRecordInput.reflection = $event"
            />
          </div>
          <div class="w-4/5 mx-auto flex flex-row items-center">
            <h2 class="font-normal text-lg w-32">{{ thirdRowTitle }}</h2>
            <MTextField
              type="text"
              :value="eRecordInput.description"
              :invalid="!eValidRecordDescriptionInput"
              invalid-message="入力してください。"
              :entered="eRecordDescriptionEntered"
              @enter="
                () => {
                  eRecordDescriptionEntered = true;
                  if (eRecordInput.description.length > 0) {
                    eValidRecordDescriptionInput = true;
                  } else {
                    eValidRecordDescriptionInput = false;
                  }
                }
              "
              @input="eRecordInput.description = $event"
            />
          </div>
        </div>
        <div
          v-else-if="status === 'unitReflectionInput'"
          class="reflection-wrapper"
        >
          <h1 class="font-medium text-2xl text-center mb-8">
            {{
              translatedValue(
                generateObjectPath("registerReflectionForUnitName"),
                {
                  unitName
                }
              )
            }}
          </h1>
          <div class="w-4/5 mx-auto flex flex-row items-center">
            <h2 class="font-normal text-lg w-32">
              {{ translatedValue(generateObjectPath("reflection")) }}
            </h2>
            <MTextArea
              :value="unitReflection"
              height="tall"
              @input="unitReflection = $event"
              @enter="
                () => {
                  if (unit && unit.data.selfReflection) {
                    validUnitReflection = true;
                  } else {
                    if (unitReflection.length > 0) {
                      validUnitReflection = true;
                    } else {
                      validUnitReflection = false;
                    }
                  }
                }
              "
            />
          </div>
        </div>
        <div v-else-if="status === 'heartRate'" class="reflection-wrapper">
          <div v-if="targetHeartRateData.length > 0">
            <h2 class="text-xl mb-4">
              {{ heartRateTitle }}
            </h2>
            <MSLineChart
              v-if="reflections.length > 0"
              class="line-chart-canvas"
              :labels="targetHeartRatesLabels"
              :datasets="targetHeartRatesDatasets"
              :min="targetHeartRatesMin"
              :max="targetHeartRatesMax"
            />
          </div>
          <div v-else>
            該当の時間にデータはありません。
          </div>
        </div>
        <div v-else>
          <p>問題が発生しました。画面をリロードしてやり直してみてください。</p>
        </div>
        <div
          v-if="status === 'display'"
          class="buttons absolute flex flex-row top-0 right-0"
        >
          <MButton class="mr-2" @click="openRecordInput">{{
            translatedValue(generateObjectPath("registerRecord"))
          }}</MButton>
          <MButton @click="handleUnitReflectionInputs">{{
            unit && unit.data.selfReflection
              ? translatedValue(generateObjectPath("updateReflection"))
              : translatedValue(generateObjectPath("registerReflection"))
          }}</MButton>
        </div>
      </main>
    </template>
    <template #footer>
      <div v-if="status === 'display'">
        <!-- there is nothing to show -->
      </div>
      <div v-else-if="status === 'recordInput'" class="flex flex-row gap-2">
        <MButton
          negative
          @click="
            () => {
              clearRecordInput();
              status = 'display';
            }
          "
          >{{ translatedValue(generateObjectPath("cancel")) }}</MButton
        >
        <MButton :invalid="!validReflectionInput" @click="registerRecord">{{
          translatedValue(generateObjectPath("register"))
        }}</MButton>
      </div>
      <div v-else-if="status === 'recordEdit'" class="flex flex-row gap-2">
        <MButton
          negative
          @click="
            () => {
              clearRecordEditInput();
              status = 'display';
            }
          "
          >{{ translatedValue(generateObjectPath("cancel")) }}</MButton
        >
        <MButton :invalid="!eValidReflectionInput" @click="updateRecord">{{
          translatedValue(generateObjectPath("update"))
        }}</MButton>
        <MButton danger @click="deleteRecord">{{
          translatedValue(generateObjectPath("delete"))
        }}</MButton>
      </div>
      <div
        v-else-if="status === 'unitReflectionInput'"
        class="flex flex-row gap-2"
      >
        <MButton negative @click="clearUnitReflectionInput">{{
          translatedValue(generateObjectPath("cancel"))
        }}</MButton>
        <MButton
          :invalid="!validUnitReflection"
          @click="registerUnitReflection"
          >{{
            unit && unit.data.selfReflection
              ? translatedValue(generateObjectPath("update"))
              : translatedValue(generateObjectPath("register"))
          }}</MButton
        >
      </div>
      <div v-else-if="status === 'heartRate'" class="flex flex-row gap-2">
        <MButton negative @click="clearHeartRateData">戻る</MButton>
      </div>
      <div v-else>
        <!-- there's nothing to show here -->
      </div>
    </template>
  </MBaseModal>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import MIcon from "@/components/MIcon.vue";
import MBaseModal from "@/components/MBaseModal.vue";
import MButton from "@/components/form/MButton.vue";
import MTextField from "@/components/form/MTextField.vue";
import MTextArea from "@/components/form/MTextArea.vue";
import { Student } from "@/entities/student";
import store from "@/store";
import "firebase/firestore";
import "firebase/functions";
import {
  Unit,
  reflectionCollectionKey,
  convertToReflection
} from "@/entities/reflection";
import MSLineChart from "@/components/analyze/MSLineChart.vue";
import dayjs from "dayjs";
import "dayjs/locale/ja";
import firebase from "firebase/app";
import { isProd } from "@/env";
import {
  fillMissingTimeRange,
  generateReflectionUid,
  getUnixFromUid,
  isFutureDate,
  ReflectionWithTimeRange,
  sortReflectionsByUid,
  validateTimeRange
} from "@/utils/reflection";
import { getHeartRateDataWithTimeRange } from "@/api/polar";
import { DeviceHeartRateData } from "@/entities/polar";
dayjs.locale("ja");

const MAX_LENGTH = 100000;

@Options({
  components: {
    MIcon,
    MBaseModal,
    MButton,
    MTextField,
    MSLineChart,
    MTextArea
  },
  emits: ["close", "aiReflectionUpdate"],
  props: {
    student: Object,
    unit: Object,
    canEdit: Boolean
  }
})
export default class MsEditModal extends Vue {
  student!: Student;
  unit: Unit | null = null;
  reflections: ReflectionWithTimeRange[] = [];
  datasets: {
    label: string;
    data: number[];
    borderColor: string;
    borderWidth: number;
    tension: number;
  }[] = [];
  labels: string[] = [];
  min = 0;
  max = 100;
  canEdit = false;
  unitName = "";
  graphTitle = "";
  secondRowTitle = "";
  thirdRowTitle = "";
  status:
    | "display"
    | "recordInput"
    | "recordEdit"
    | "unitReflectionInput"
    | "unitReflectionEdit"
    | "heartRate" = "display";
  promptTrimmed = false;

  generateObjectPath(propName: string) {
    return "learningCard.modal" + "." + propName;
  }

  get hasPolarDeviceid(): boolean {
    return !!this.student.data.polarDeviceId;
  }

  get translatedValue() {
    return store.state.translatedValue;
  }

  getMaxLength() {
    return MAX_LENGTH;
  }

  close() {
    this.$emit("close");
  }

  formatDate(uid: string): string {
    const unix = getUnixFromUid(uid);
    return dayjs.unix(unix).format("MM/DD(ddd)");
  }

  async getReflections() {
    if (!this.unit) return [];
    const snapshot = await this.unit.ref
      .collection(reflectionCollectionKey)
      .get();
    if (snapshot.empty) return [];
    return snapshot.docs
      .filter(doc => doc.exists && doc.data())
      .map(doc => convertToReflection(doc.data(), doc.ref));
  }

  // 記録登録系統
  recordInput: {
    timeRangeStart: string;
    timeRangeEnd: string;
    record: number;
    reflection: string;
    description: string;
  } = {
    timeRangeStart: dayjs()
      .startOf("day")
      .format("YYYY-MM-DDTHH:mm"),
    timeRangeEnd: dayjs()
      .endOf("day")
      .format("YYYY-MM-DDTHH:mm"),
    record: 0,
    reflection: "",
    description: ""
  };
  recordEntered = true;
  recordReflectionEntered = false;
  recordDescriptionEntered = false;
  validRecordInput = true;
  validRecordReflectionInput = false;
  validRecordDescriptionInput = false;

  get validTimeRangeInput() {
    const startUnix = dayjs(this.recordInput.timeRangeStart).unix();
    const endUnix = dayjs(this.recordInput.timeRangeEnd).unix();
    return validateTimeRange(startUnix, endUnix);
  }

  get validReflectionInput() {
    return (
      this.validRecordInput &&
      this.validRecordReflectionInput &&
      this.validRecordDescriptionInput &&
      this.validTimeRangeInput
    );
  }
  openRecordInput() {
    this.status = "recordInput";
  }
  clearRecordInput() {
    this.recordInput = {
      timeRangeStart: dayjs()
        .startOf("day")
        .format("YYYY-MM-DDTHH:mm"),
      timeRangeEnd: dayjs()
        .endOf("day")
        .format("YYYY-MM-DDTHH:mm"),
      record: 0,
      reflection: "",
      description: ""
    };
    this.recordEntered = true;
    this.recordReflectionEntered = false;
    this.recordDescriptionEntered = false;
    this.validRecordInput = true;
    this.validRecordReflectionInput = false;
    this.validRecordDescriptionInput = false;
  }
  async registerRecord() {
    if (!this.unit) return;
    if (!this.validReflectionInput) return;
    if (this.recordInput.reflection.length === 0) {
      alert("振り返りが入力されていません。");
      return;
    }
    if (this.recordInput.description.length === 0) {
      alert("自由入力が入力されていません。");
      return;
    }

    const startUnix = dayjs(this.recordInput.timeRangeStart).unix();
    const endUnix = dayjs(this.recordInput.timeRangeEnd).unix();

    if (!validateTimeRange(startUnix, endUnix)) {
      alert("開始日時は終了日時より前である必要があります。");
      return;
    }

    if (isFutureDate(startUnix) || isFutureDate(endUnix)) {
      alert("未来の日付は登録できません。");
      return;
    }

    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", "登録中...");

      const docId = generateReflectionUid(
        this.reflections,
        dayjs(this.recordInput.timeRangeStart).format("YYYY-MM-DD")
      );
      const newRef = this.unit.ref
        .collection(reflectionCollectionKey)
        .doc(docId);
      await newRef.set({
        uid: newRef.id,
        record: this.recordInput.record,
        reflection: this.recordInput.reflection,
        description: this.recordInput.description,
        timeRange: {
          start: startUnix,
          end: endUnix
        },
        createdAt: startUnix,
        updatedAt: dayjs().unix()
      });
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
      this.$router.go(0);
    } catch (e) {
      alert("記録の登録に失敗しました。");
      console.error(e);
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
    }
  }

  //記録編集系統
  reflectionToEdit: ReflectionWithTimeRange | null = null;
  eRecordInput: {
    timeRangeStart: string;
    timeRangeEnd: string;
    record: number;
    reflection: string;
    description: string;
    createdAt: number;
  } = {
    timeRangeStart: dayjs()
      .startOf("day")
      .format("YYYY-MM-DDTHH:mm"),
    timeRangeEnd: dayjs()
      .endOf("day")
      .format("YYYY-MM-DDTHH:mm"),
    record: 0,
    reflection: "",
    description: "",
    createdAt: 0
  };
  eRecordEntered = true;
  eRecordReflectionEntered = true;
  eRecordDescriptionEntered = true;
  eValidRecordInput = true;
  eValidRecordReflectionInput = true;
  eValidRecordDescriptionInput = true;

  get eValidTimeRangeInput() {
    const startUnix = dayjs(this.eRecordInput.timeRangeStart).unix();
    const endUnix = dayjs(this.eRecordInput.timeRangeEnd).unix();
    return validateTimeRange(startUnix, endUnix);
  }

  get eValidReflectionInput() {
    return (
      this.eValidRecordInput &&
      this.eValidRecordReflectionInput &&
      this.eValidRecordDescriptionInput &&
      this.eValidTimeRangeInput
    );
  }
  openRecordEditInput(reflectionId: string) {
    if (!reflectionId) return;
    if (!this.reflections.length) return;
    const targetReflection = this.reflections.find(
      r => r.data.uid === reflectionId
    );
    if (!targetReflection) {
      alert(`${reflectionId}の記録が見つかりません。`);
      return;
    }
    this.reflectionToEdit = { ...targetReflection } as ReflectionWithTimeRange;
    const { data } = targetReflection;
    this.eRecordInput = {
      timeRangeStart: dayjs
        .unix(data.timeRange.start)
        .format("YYYY-MM-DDTHH:mm"),
      timeRangeEnd: dayjs.unix(data.timeRange.end).format("YYYY-MM-DDTHH:mm"),
      record: data.record,
      reflection: data.reflection,
      description: data.description,
      createdAt: data.createdAt
    };
    this.status = "recordEdit";
  }
  clearRecordEditInput() {
    this.reflectionToEdit = null;
    this.eRecordInput = {
      timeRangeStart: dayjs()
        .startOf("day")
        .format("YYYY-MM-DDTHH:mm"),
      timeRangeEnd: dayjs()
        .endOf("day")
        .format("YYYY-MM-DDTHH:mm"),
      record: 0,
      reflection: "",
      description: "",
      createdAt: 0
    };
    this.eRecordEntered = true;
    this.eRecordReflectionEntered = true;
    this.eRecordDescriptionEntered = true;
    this.eValidRecordInput = true;
    this.eValidRecordReflectionInput = true;
    this.eValidRecordDescriptionInput = true;
  }
  async updateRecord() {
    if (!this.unit) return;
    if (!this.reflectionToEdit) return;
    if (!this.eValidReflectionInput) return;

    if (this.eRecordInput.reflection.length === 0) {
      alert("振り返りが入力されていません。");
      return;
    }
    if (this.eRecordInput.description.length === 0) {
      alert("自由入力が入力されていません。");
      return;
    }

    const startUnix = dayjs(this.eRecordInput.timeRangeStart).unix();
    const endUnix = dayjs(this.eRecordInput.timeRangeEnd).unix();

    if (!validateTimeRange(startUnix, endUnix)) {
      alert("開始日時は終了日時より前である必要があります。");
      return;
    }

    if (isFutureDate(startUnix) || isFutureDate(endUnix)) {
      alert("未来の日付は登録できません。");
      return;
    }

    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", "更新中...");

      const startDate = dayjs(this.eRecordInput.timeRangeStart).format(
        "YYYY-MM-DD"
      );
      const originalDate = dayjs
        .unix(this.reflectionToEdit.data.timeRange.start)
        .format("YYYY-MM-DD");

      if (originalDate !== startDate) {
        const newDocId = generateReflectionUid(this.reflections, startDate);
        // 日付に変更がある場合は、新しく作って、今のを削除する
        const newRef = this.unit.ref
          .collection(reflectionCollectionKey)
          .doc(newDocId);
        await Promise.all([
          newRef.set({
            uid: newRef.id,
            record: this.eRecordInput.record,
            reflection: this.eRecordInput.reflection,
            description: this.eRecordInput.description,
            timeRange: {
              start: startUnix,
              end: endUnix
            },
            createdAt: this.eRecordInput.createdAt,
            updatedAt: dayjs().unix()
          }),
          this.reflectionToEdit.ref.delete()
        ]);
      } else {
        await this.reflectionToEdit.ref.update({
          record: this.eRecordInput.record,
          reflection: this.eRecordInput.reflection,
          description: this.eRecordInput.description,
          timeRange: {
            start: startUnix,
            end: endUnix
          },
          updatedAt: dayjs().unix()
        });
      }

      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
      this.$router.go(0);
    } catch (e) {
      alert("記録の更新に失敗しました。");
      console.error(e);
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
    }
  }

  async deleteRecord() {
    if (!this.unit) return;
    if (!this.reflectionToEdit) return;
    if (!window.confirm("この振り返りを削除しても良いですか？")) return;
    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", "削除中...");
      await this.reflectionToEdit.ref.delete();
    } catch (e) {
      alert("削除に失敗しました。");
      console.error(e);
    } finally {
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
      this.$router.go(0);
    }
  }

  // 振り返り登録系統
  handleUnitReflectionInputs() {
    if (!this.unit) return;
    // 将来的に処理の内容を切り替える必要があるかもしれないので分岐を分けておく
    if (this.unit.data.selfReflection) {
      this.openUnitReflectionInput();
    } else {
      this.openUnitReflectionInput();
    }
  }
  unitReflection = "";
  validUnitReflection = false;
  openUnitReflectionInput() {
    this.unitReflection =
      this.unit && this.unit.data.selfReflection
        ? this.unit.data.selfReflection
        : "";
    this.validUnitReflection = this.unitReflection.length > 0 ? true : false;
    this.status = "unitReflectionInput";
  }
  clearUnitReflectionInput() {
    this.unitReflection = "";
    this.validUnitReflection = false;
    this.status = "display";
  }
  async registerUnitReflection() {
    if (!this.unit) return;
    if (!this.validUnitReflection) return;
    if (!this.unit.data.selfReflection && this.unitReflection.length === 0) {
      alert("振り返りが入力されていません。");
      return;
    }
    const text = this.unit.data.selfReflection ? "更新" : "登録";
    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", `振り返り${text}中...`);
      await this.unit.ref.update({ selfReflection: this.unitReflection });
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
      this.$router.go(0);
    } catch (e) {
      alert(`振り返りの${text}に失敗しました。`);
      console.error(e);
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
    }
  }

  //心拍数データ
  targetHeartRateData: {
    time: number;
    hr: number;
  }[] = [];
  get targetHeartRatesLabels() {
    if (!this.targetHeartRateData) return [];
    return this.targetHeartRateData.map(hr =>
      dayjs.unix(hr.time).format("HH:mm:ss")
    );
  }

  get targetHeartRatesDatasets() {
    if (!this.targetHeartRateData) return [];

    return [
      {
        label: "心拍数",
        data: this.targetHeartRateData.map(hr => hr.hr),
        borderColor: "rgb(75, 192, 192)",
        borderWidth: 2,
        tension: 0.1
      }
    ];
  }

  get heartRateTitle(): string {
    return this.targetHeartRateData.length > 0
      ? `心拍数の推移 ${dayjs
          .unix(this.targetHeartRateData[0].time)
          .format("YYYY/MM/DD HH:mm:ss")} ～ ${dayjs
          .unix(
            this.targetHeartRateData[this.targetHeartRateData.length - 1].time
          )
          .format("YYYY/MM/DD HH:mm:ss")}`
      : "";
  }

  get targetHeartRatesMin() {
    if (!this.targetHeartRateData) return 0;

    const buffer = 5;
    return Math.min(...this.targetHeartRateData.map(_ => _.hr)) - buffer;
  }

  get targetHeartRatesMax() {
    if (!this.targetHeartRateData) return 0;

    const buffer = 5;
    return Math.max(...this.targetHeartRateData.map(_ => _.hr)) + buffer;
  }

  async openHeartRateData(reflectionId: string) {
    if (!reflectionId) return;
    if (!this.reflections.length) return;
    const targetReflection = this.reflections.find(
      r => r.data.uid === reflectionId
    );
    if (!targetReflection) {
      alert(`${reflectionId}の記録が見つかりません。`);
      return;
    }

    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", "データ取得中...");

      const { timeRange } = targetReflection.data;
      const res = await getHeartRateDataWithTimeRange({
        from: timeRange.start,
        to: timeRange.end
      });

      if (!res.history) {
        this.targetHeartRateData = [];
        this.status = "heartRate";
        return;
      }

      this.targetHeartRateData = Object.entries(res.history).map(
        ([time, hr]) => ({
          time: dayjs(time).unix(),
          hr
        })
      );

      this.status = "heartRate";
    } catch (e) {
      alert(
        "データの取得に失敗しました。" +
          "\n" +
          (e instanceof Error ? e.message : "unknown error")
      );
    } finally {
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
    }
  }

  clearHeartRateData() {
    this.targetHeartRateData = [];
    this.status = "display";
  }

  //AI振り返り系統
  async getHeartRatesFromReflections(
    reflections: ReflectionWithTimeRange[]
  ): Promise<DeviceHeartRateData[]> {
    if (!reflections.length) return [];

    return (
      await Promise.all(
        reflections.map(({ data }) =>
          getHeartRateDataWithTimeRange({
            from: data.timeRange.start,
            to: data.timeRange.end
          })
        )
      )
    ).filter(
      data => data.history !== null && Object.keys(data.history).length > 0
    );
  }

  parseHeartRateData(
    heartRateData: DeviceHeartRateData[],
    options?: { taleEvery?: number }
  ): string {
    return heartRateData
      .map(({ history }) =>
        history
          ? Object.entries(history).map(([time, hr]) => ({
              time: dayjs(time).unix(),
              hr
            }))
          : null
      )
      .filter(_ => _ !== null)
      .map(
        // dataはnon-nullで確定だがeslintが通してくれないのでやむを得ずアサーション
        data => `
${dayjs.unix(data![0].time).format("YYYY/MM/DD")}
${data!
  .filter((_, i) =>
    options?.taleEvery !== undefined ? i % options.taleEvery === 0 : true
  )
  .map(({ time, hr }) => `${dayjs.unix(time).format("HH:mm:ss")} : ${hr}`)
  .join("\n")}
`
      )
      .join("=============");
  }

  async getGptMessage(): Promise<{ message: string; promptTrimmed: boolean }> {
    if (!this.unit) throw new Error("単元がありません。");
    if (this.reflections.length === 0)
      throw new Error("振り返りデータがありません。");
    let trimmed = false;
    let prompt = `
# 役割
あなたは授業をサポートするチューターです。生徒の学習記録に対してコメントし、生徒の振り返りをサポートする役割です。
# 指示
以下の手順で、生徒の授業での学習記録を元にポジティブなコメントを書いてください。
1. 生徒の最新の取り組みと数値データ（例：50m走の結果や提出物の数）を確認します。
2. 観点別評価の項目ごとに、生徒がどのように成長しているかを具体的に示します。
3. 観点別評価の項目ごとに、生徒の努力や改善が見られるポイントを強調して称賛します。
4. 観点別評価の項目ごとに、前回の記録と比較して進歩が見られる場合、その進歩を具体的に示し、さらに前向きなコメントを付け加えます。
5. 生徒がこれからも努力を続けられるように励ましのメッセージを付け加えます。
# 観点別評価
1. 知識・理解
   - 運動の合理的な実践に関する具体的な知識や、生涯にわたって運動に親しむための科学的知識、及び運動の特性に応じた基本的な技能を身に付けている。
   - 個人生活における健康・安全について、課題解決に役立つ知識や技能を身に付けている。
2. 思考・判断・表現
   - 自己の課題に応じた運動の取り組み方や、健康の保持及び体力を高めるための運動の組み合わせ方を工夫し、それらを表現している。
   - 個人生活における健康課題を把握し、その解決を目指して科学的に考え、判断し、それらを表現している。
3. 主体的に学習に取り組む態度
   - 運動の楽しさや喜びを味わうことができるよう、運動の合理的な実践に主体的に取り組もうとしている。
   - 自他の健康を大切にし、健康の保持増進や回復に関する学習活動に主体的に取り組もうとしている。
# 制約条件
- コメントはこの文章で完結する内容とし、生徒からの返答を求めないようにしてください。
- コメントは前向きで励みになる内容にしてください。
- コメントは200~300文字とする。
`;

    if (this.hasPolarDeviceid) {
      prompt =
        prompt +
        `- 心拍数データがあれば分析し、返答の中で言及してください。
  `;
    }

    let result = "";
    result =
      "#生徒の学習記録" + "\n" + `【単元名】: ${this.unit.data.name}` + "\n\n";
    result =
      result +
      `【${this.graphTitle}】: ` +
      "\n" +
      `${this.reflections
        .map(
          reflection =>
            dayjs.unix(getUnixFromUid(reflection.data.uid)).format("MM月DD日") +
            " : " +
            String(reflection.data.record)
        )
        .join("\n")}` +
      "\n\n";
    result =
      result +
      `【${this.secondRowTitle}】` +
      "\n" +
      `${this.reflections
        .map(
          reflection =>
            dayjs.unix(getUnixFromUid(reflection.data.uid)).format("MM月DD日") +
            " : " +
            reflection.data.reflection
        )
        .join("\n")}` +
      "\n\n";
    result =
      result +
      `【${this.thirdRowTitle}】` +
      "\n" +
      `${this.reflections
        .map(
          reflection =>
            dayjs.unix(getUnixFromUid(reflection.data.uid)).format("MM月DD日") +
            " : " +
            reflection.data.description
        )
        .join("\n")}` +
      "\n\n";

    if (this.hasPolarDeviceid) {
      const heartRates = await this.getHeartRatesFromReflections(
        this.reflections
      );
      result =
        result +
        "\n" +
        "【心拍数の記録】※振り返り日と実際の記録の日付が一致しないことがあります。" +
        this.parseHeartRateData(heartRates, { taleEvery: 5 });

      // MAX_LENGTH以上になったら強制的にお尻から切り取り
      if (result.length > MAX_LENGTH) {
        trimmed = true;
        result = result.slice(0, MAX_LENGTH);
      }
    }

    if (store.state.lang === "en") {
      result =
        result +
        "\n\n" +
        `
      # 必須条件
      返答は必ず英語で返してください。
      `;
    }
    result = result.replace(/^[ \t]+/gm, "");

    const model = "azure-gpt-5-mini";

    if (!isProd) {
      console.warn("送信されるプロンプト ※商用では表示されません");
      console.log(prompt);
      console.log(result);
      console.log(`model: ${model}`);
    }

    const call = firebase
      .app()
      .functions("asia-northeast1")
      .httpsCallable("get_ai_message");
    const res = await call({ prompt, messageText: result, model });
    return { message: res.data.message as string, promptTrimmed: trimmed };
  }

  async updateAiReflection(option: { actionType: "generate" | "delete" }) {
    if (!this.unit) return;
    if (option.actionType !== "generate" && option.actionType !== "delete")
      return;
    const isGenerating = option.actionType === "generate";
    const text = isGenerating ? "作成" : "削除";
    try {
      store.commit("SET_LOADING", true);
      store.commit(
        "SET_LOAD_TEXT",
        `AI振り返り${text}中...(このままお待ちください)`
      );

      const { message = "", promptTrimmed: trimmed = false } = isGenerating
        ? await this.getGptMessage()
        : {};

      await this.unit.ref.update({ aiReflection: message });
      this.$emit("aiReflectionUpdate", {
        unitId: this.unit.data.uid,
        content: message
      });

      if (trimmed) {
        this.promptTrimmed = true;
        setTimeout(() => {
          this.promptTrimmed = false;
        }, 1000 * 5);
      }
    } catch (e) {
      alert(
        `AI振り返りの${text}に失敗しました。` +
          "\n" +
          (e.message ?? "不明なエラー")
      );
      console.error(e);
    } finally {
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
    }
  }

  generateAiReflection() {
    if (!this.unit) return;
    if (!this.unit.data.selfReflection) {
      alert(
        "AIの振り返りを利用するには、単元の自身の振り返りを記入してください。"
      );
      return;
    }
    if (this.unit.data.aiReflection) {
      const res = window.confirm(
        "現在登録されている振り返りが上書きされますが、よろしいですか？"
      );
      if (!res) return;
    }

    this.updateAiReflection({ actionType: "generate" });
  }

  deleteAiReflection() {
    const res = window.confirm("AI振り返りを削除してもよろしいですか？");
    if (!res) return;
    this.updateAiReflection({ actionType: "delete" });
  }

  async created(): Promise<void> {
    if (!this.student) {
      alert("生徒情報が取得できません。");
      this.$emit("close");
    }
    if (!this.unit) {
      alert("単元情報が取得できません。");
      this.$emit("close");
    }
    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", "情報取得中...");
      const _unit = { ...this.unit } as Unit;
      this.unitName = _unit.data.name;
      this.graphTitle = _unit.data.graphTitle || "数値(記録)";
      this.secondRowTitle = _unit.data.secondRowTitle || "授業ごとの振り返り";
      this.thirdRowTitle = _unit.data.thirdRowTitle || "自由入力";

      const _reflections = await this.getReflections();
      this.reflections = sortReflectionsByUid(_reflections).map(reflection =>
        fillMissingTimeRange(reflection)
      );

      if (this.reflections.length > 0) {
        const records = this.reflections.map(_ => +_.data.record);
        if (this.reflections.length > 1) {
          this.min = Math.min(...records) - 1;
          this.max = Math.max(...records) + 1;
        } else {
          const record = +this.reflections[0].data.record;
          this.min = record < 0 ? record - 1 : 0;
          this.max = record + 5;
        }
      }

      this.labels =
        this.reflections.length > 0
          ? this.reflections.map(_ =>
              dayjs.unix(getUnixFromUid(_.data.uid)).format("MM-DD")
            )
          : [];
      this.datasets =
        this.reflections.length > 0
          ? [
              {
                label: this.unitName,
                data: this.reflections.map(_ => _.data.record),
                borderColor: "rgb(75, 192, 192)",
                borderWidth: 2,
                tension: 0.1
              }
            ]
          : [];

      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
    } catch (e) {
      console.error(e);
      alert("情報の取得に失敗しました。");
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
      this.$emit("close");
    }
  }
}
</script>

<style lang="scss" scoped>
.reflection-wrapper {
  width: 100%;
  max-width: 780px;
  margin: 0 auto;
}
.line-chart-canvas {
  width: 80%;
  margin: 0 auto;
}
.pt-none {
  padding-top: 0 !important;
}

/* フェードアニメーション */
.fade-enter-active {
  transition: opacity 0.3s ease;
}

.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
